import type { FuryRelease, UserRole } from '../core/interfaces';
import { normalizeClientId } from '../shared/utils/client-id.util';

/** Days after `releasedAt` a non-welcome release stays eligible when it has no explicit schedule. */
export const DEFAULT_RELEASE_WINDOW_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The viewer attributes a release is matched against. */
export interface ReleaseViewer {
  roles: readonly UserRole[];
  /** Account tenant id, already normalized; `null` for internal users. */
  clientId: string | null;
}

/** Whether the viewer satisfies the release's audience. Omitted fields are unrestricted; declared fields are ANDed. */
export function matchesAudience(release: FuryRelease, viewer: ReleaseViewer): boolean {
  const { roles, clientIds } = release.audience ?? {};
  if (roles && !roles.some((role) => viewer.roles.includes(role))) return false;
  if (clientIds) {
    if (!viewer.clientId) return false;
    if (!clientIds.map(normalizeClientId).includes(viewer.clientId)) return false;
  }
  return true;
}

/** Whether `now` falls inside the release's schedule, or its default window; welcome releases always do. */
export function isWithinSchedule(release: FuryRelease, now: number): boolean {
  if (release.kind === 'welcome') return true;
  if (release.schedule) {
    return now >= Date.parse(release.schedule.from) && now <= Date.parse(release.schedule.until);
  }
  const released = new Date(release.releasedAt).getTime();
  if (Number.isNaN(released)) return false;
  return now <= released + DEFAULT_RELEASE_WINDOW_DAYS * DAY_MS;
}

/** Audience and schedule combined. */
export function isReleaseVisible(release: FuryRelease, viewer: ReleaseViewer, now: number): boolean {
  return matchesAudience(release, viewer) && isWithinSchedule(release, now);
}

/**
 * The releases a viewer should see. On a first visit only the eligible welcome release
 * is returned (no history, no window), evaluated after the audience filter.
 */
export function visibleReleases(
  releases: readonly FuryRelease[],
  viewer: ReleaseViewer,
  now: number,
  isFirstVisit: boolean,
): FuryRelease[] {
  const audienceMatches = releases.filter((release) => matchesAudience(release, viewer));
  if (isFirstVisit) return audienceMatches.filter((release) => release.kind === 'welcome');
  return audienceMatches.filter((release) => isWithinSchedule(release, now));
}
